/**
 * EPIC 47 — app-bar MESSAGES menu, mounted right next to the NotificationBell in
 * the admin + worker shells. Mirrors the bell's IconButton + Badge + Menu(list)
 * shape but is a SEPARATE, role-aware surface over /conversations (the bell is
 * per-notification; this is per-thread).
 *
 * Role-aware data source + unread counter (see lib/messagesMenu.ts):
 *   • staff (admin/dispatcher/finance) — useAllConversations(); unread = staffUnread;
 *     opening a thread deep-links to /messages?open=<id>.
 *   • worker — useMyConversations(uid) (participantUids array-contains, single-where
 *     so no composite index); unread = unreadBy[uid]; opening links to /worker/messages
 *     (the worker's single thread).
 * Client is out of scope for now — ClientShell renders no NotificationBell, so no
 * MessagesMenu either (order/site threads stay on their per-card surfaces).
 *
 * The dropdown lists the most-recent threads (both UNREAD "active" and already-read):
 * unread rows are bold with an accent background + a count chip; read rows are muted.
 * A "Vaata kõiki" footer opens the full inbox.
 */

import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import {
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Conversation } from '@rendimees24/shared-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import {
  conversationUnread,
  recentConversations,
  totalUnread,
  type MessagesViewpoint,
} from '../lib/messagesMenu';
import { useAllConversations, useMyConversations } from '../services/useMessaging';

import { BADGE_MAX, unreadLabel } from './unreadLabel';

const MAX_ROWS = 8;

function fmtTime(v: unknown): string {
  let ms = 0;
  if (v && typeof v === 'object' && 'toMillis' in v && typeof (v as { toMillis: () => number }).toMillis === 'function') {
    ms = (v as { toMillis: () => number }).toMillis();
  } else if (typeof v === 'string') {
    ms = Date.parse(v) || 0;
  }
  if (!ms) return '';
  return new Date(ms).toLocaleString('et-EE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Presentational menu — data + viewpoint are injected by the role wrappers. */
function MessagesMenuView({
  conversations,
  viewpoint,
  uid,
}: {
  conversations: Conversation[];
  viewpoint: MessagesViewpoint;
  uid: string;
}) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const badge = totalUnread(conversations, uid, viewpoint);
  const recent = recentConversations(conversations, MAX_ROWS);

  const subjectOf = (c: Conversation): string => {
    if (viewpoint === 'worker') return t('messagesMenu.dispatch');
    if (c.subjectName) return c.subjectName;
    return c.type === 'order'
      ? t('messagesMenu.typeOrder')
      : c.type === 'site'
        ? t('messagesMenu.typeSite')
        : t('messagesMenu.typeWorker');
  };

  const preview = (c: Conversation): string => {
    const text = c.lastMessage?.text?.trim();
    if (text) return text.length > 60 ? `${text.slice(0, 60)}…` : text;
    if (c.lastMessage?.hasAttachment) return `📎 ${t('messaging.attachment')}`;
    return t('messagesMenu.noMessagesYet');
  };

  const openThread = (c: Conversation) => {
    setAnchor(null);
    if (viewpoint === 'worker') navigate('/worker/messages');
    else navigate(`/messages?open=${encodeURIComponent(c.id)}`);
  };

  const openAll = () => {
    setAnchor(null);
    navigate(viewpoint === 'worker' ? '/worker/messages' : '/messages');
  };

  return (
    <>
      <Tooltip title={t('messagesMenu.title') ?? ''}>
        <IconButton
          aria-label={unreadLabel(t, t('messagesMenu.title'), badge)}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          {badge > 0 ? (
            <Badge color="error" badgeContent={badge} max={BADGE_MAX}>
              <ForumOutlinedIcon />
            </Badge>
          ) : (
            <ForumOutlinedIcon />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 340, maxWidth: '90vw' } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('messagesMenu.title')}
          </Typography>
        </Box>
        <Divider />
        {recent.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('messagesMenu.empty')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {recent.map((c) => {
              const unread = conversationUnread(c, uid, viewpoint);
              const isUnread = unread > 0;
              return (
                <ListItemButton
                  key={c.id}
                  onClick={() => openThread(c)}
                  sx={{ alignItems: 'flex-start', bgcolor: isUnread ? 'action.hover' : 'transparent' }}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {isUnread && (
                        <Box
                          component="span"
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: isUnread ? 700 : 500, flexGrow: 1 }}
                        noWrap
                      >
                        {subjectOf(c)}
                      </Typography>
                      {isUnread && (
                        <Chip
                          size="small"
                          color="error"
                          label={unread}
                          sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                    </Stack>
                    <Typography
                      variant="caption"
                      color={isUnread ? 'text.primary' : 'text.secondary'}
                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {preview(c)}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {fmtTime(c.updatedAt)}
                    </Typography>
                  </Stack>
                </ListItemButton>
              );
            })}
          </List>
        )}
        <Divider />
        <ListItemButton onClick={openAll} sx={{ justifyContent: 'center' }}>
          <Typography variant="body2" color="primary">
            {t('messagesMenu.viewAll')}
          </Typography>
        </ListItemButton>
      </Menu>
    </>
  );
}

/** Staff wrapper — every conversation type, unread via the shared staffUnread. */
function StaffMessagesMenu() {
  const { user } = useAuth();
  const { conversations } = useAllConversations();
  return <MessagesMenuView conversations={conversations} viewpoint="staff" uid={user?.uid ?? ''} />;
}

/** Worker wrapper — the worker's own participant threads, unread via unreadBy[uid]. */
function WorkerMessagesMenu() {
  const { user } = useAuth();
  const { conversations } = useMyConversations(user?.uid);
  return <MessagesMenuView conversations={conversations} viewpoint="worker" uid={user?.uid ?? ''} />;
}

export function MessagesMenu({ variant }: { variant: 'staff' | 'worker' }) {
  return variant === 'staff' ? <StaffMessagesMenu /> : <WorkerMessagesMenu />;
}
